import type { DealerInfoResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { dealerInfoApi } from "../../../api/client";
import { dealerInfoKeys } from "./dealerInfoKeys";

/**
 * Shared queryOptions for the Dealer Information popup (key + queryFn).
 * Backed by the generated DealerInfoApi.getDealerInfo. Gated on `enabled` in
 * addition to `approvalId` so callers only fetch while the modal that shows
 * this data is actually open (mirrors `useViewBureau`'s `enabled` option) —
 * there's no reason to hit dealer-management on every deal load.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {boolean} enabled - Whether the query should run (e.g. the modal is open).
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function dealerInfoQuery(approvalId: string, enabled: boolean) {
  return queryOptions({
    queryKey: dealerInfoKeys.detail({ approvalId }),
    queryFn: async ({ signal }): Promise<DealerInfoResponse> => {
      const response = await dealerInfoApi.getDealerInfo(approvalId, { signal });
      return response.data;
    },
    enabled: enabled && !!approvalId,
  });
}

/**
 * Reads the Dealer Information popup payload for a deal. Disabled until both
 * `approvalId` is present and `enabled` is true.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {boolean} enabled - Whether the query should run (e.g. the modal is open).
 * @returns {ReturnType<typeof useQuery<DealerInfoResponse>>} The TanStack Query result.
 */
export function useDealerInfo(approvalId: string, enabled: boolean) {
  return useQuery(dealerInfoQuery(approvalId, enabled));
}
